/**
 * Inbox (S60), New request (S61), a request with its timeline, thread and approval (S62) and a C-8 decision
 * (S64). Change requests and support are one thing, a Request (docs/50 50-D5).
 */
import { BeforeAfter, Banner, Button, Card, Chip, EmptyState, Icon, List, Row, TextArea, TextField, Timeline, type IconName } from '@ff/app-ui';
import type { requests as Q } from '@ff/contracts';
import type { JSX } from 'preact';
import { useRef, useState } from 'preact/hooks';
import type { z } from 'zod';
import { errorText, idemKey } from '../lib/api.ts';
import { invalidate, online, send, setCached, useQuery, useSend } from '../lib/data.ts';
import { rowTime, shortDate, timeLabel } from '../lib/format.ts';
import { enqueue } from '../lib/outbox.ts';
import { location, scoped } from '../lib/router.ts';
import { can, useReady } from '../lib/session.ts';
import { useStore } from '../lib/store.ts';
import { haptic, toast, tz } from '../lib/ui.ts';
import { uploadBlob, processPhoto } from '../lib/upload.ts';
import { S } from '@ff/app-ui/copy';
import { Loaded, LoadError, PrimaryAction, Screen, Section } from '../ui/common.tsx';
import { Split } from '../ui/split.tsx';

type Item = z.infer<typeof Q.InboxItem>;
type Detail = z.infer<typeof Q.RequestDetail>;
type Quick = z.infer<typeof Q.QuickRequest>;

const KIND_ICON: Record<Item['kind'], IconName> = { request: 'inbox', approval: 'check', decision: 'flag', message: 'sms', report: 'file' };

export function InboxScreen({ id }: { id?: string }): JSX.Element {
  const detail = id ? id.startsWith('dec_') ? <DecisionView id={id} /> : <RequestView id={id} /> : null;
  return <Split list={<InboxList selected={id} />} detail={detail} />;
}

function InboxList({ selected }: { selected?: string }): JSX.Element {
  const s = useReady();
  const loc = useStore(location);
  const zone = useStore(tz);
  const q = useQuery('inbox', { query: loc.scope ? { location: loc.scope } : {} });
  const group = (title: string, id: string, items: Item[], waiting = false) =>
    items.length > 0 && (
      <Section title={title} id={id}>
        <List>
          {items.map((i) => (
            <li key={i.id}>
              <Row
                href={scoped(i.link, loc.scope)}
                current={selected === i.id}
                unread={waiting}
                unreadLabel={S.inbox.waitingOnYou}
                lead={waiting ? undefined : <Icon name={KIND_ICON[i.kind]} size={22} />}
                title={i.title}
                meta={i.promisedLabel ? S.inbox.by(i.promisedLabel) : S.inbox.kind[i.kind]}
                time={rowTime(i.at, zone)}
              />
            </li>
          ))}
        </List>
      </Section>
    );
  return (
    <Screen title={S.inbox.title} query={q}>
      <Loaded q={q}>
        {(d) =>
          d.waitingOnYou.length + d.inProgress.length + d.done.length === 0 ? (
            <EmptyState title={S.inbox.firstRunTitle} body={S.inbox.firstRun} />
          ) : (
            <div class="stack" data-gap="s">
              {d.waitingOnYou.length === 0 && (
                <Banner tone="ok" icon="check">
                  {S.inbox.nothingWaiting}
                </Banner>
              )}
              {group(S.inbox.waitingOnYou, 'g-waiting', d.waitingOnYou, true)}
              {group(S.inbox.inProgress, 'g-progress', d.inProgress)}
              {group(S.inbox.done, 'g-done', d.done)}
            </div>
          )
        }
      </Loaded>
      {can(s.membership, 'requests.create_billing') && (
        <PrimaryAction href={scoped('/inbox/new', loc.scope)}>{S.inbox.newRequest}</PrimaryAction>
      )}
    </Screen>
  );
}

// ------------------------------------------------------------------------------------------ new request

const QUICK: Quick[] = ['change_hours', 'add_service', 'fix_site', 'billing_question', 'something_else'];

export function NewRequestScreen(): JSX.Element {
  const s = useReady();
  const loc = useStore(location);
  const [quick, setQuick] = useState<Quick | null>(null);
  // A screen that opens a request with words ready (the buyout quote on What's yours) leaves them here once.
  const [body, setBody] = useState(() => {
    try {
      const v = sessionStorage.getItem('ff-request-prefill') ?? '';
      sessionStorage.removeItem('ff-request-prefill');
      return v.slice(0, 500);
    } catch {
      return '';
    }
  });
  const [error, setError] = useState<string | null>(null);
  const [photos, setPhotos] = useState<{ name: string; uploadId: string | null }[]>([]);
  const [done, setDone] = useState<{ label: string | null; queued: boolean; id: string | null } | null>(null);
  const [key] = useState(idemKey);
  const act = useSend();
  const input = useRef<HTMLInputElement>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const bookkeeper = s.membership.role === 'bookkeeper';
  const options = bookkeeper ? (['billing_question'] as Quick[]) : QUICK;

  const submit = async (e: Event) => {
    e.preventDefault();
    if (body.trim().length < 3) {
      setError(S.inbox.bodyError);
      area.current?.focus();
      return;
    }
    setError(null);
    const payload = {
      body: body.trim(),
      ...(quick ? { quick } : {}),
      ...(photos.some((p) => p.uploadId) ? { attachments: photos.filter((p) => p.uploadId).map((p) => p.uploadId!) } : {}),
      ...(loc.scope ? { locationId: loc.scope } : {}),
      idempotencyKey: key,
    };
    if (!online.get()) {
      await enqueue({ id: key, kind: 'request', body: payload, photos: [] });
      setDone({ label: null, queued: true, id: null });
      return;
    }
    const out = await act.run(() => send('requestCreate', { body: payload }));
    if (!out) return;
    haptic();
    invalidate('inbox', 'requests', 'home');
    setDone({ label: out.promisedLabel, queued: false, id: out.request.id });
  };

  if (done)
    return (
      <Screen title={done.queued ? S.jobs.queuedTitle : S.inbox.thanksTitle} back={scoped('/inbox', loc.scope)}>
        <div class="stack done-panel">
          <Icon name={done.queued ? 'upload' : 'check'} size={40} />
          <p class="t-heading">{done.queued ? S.inbox.queued : S.inbox.thanksLead(done.label ?? '')}</p>
          {done.id && <Button variant="primary" href={scoped(`/inbox/${done.id}`, loc.scope)}>{S.inbox.openRequest}</Button>}
          <Button variant="secondary" href={scoped('/inbox', loc.scope)}>{S.inbox.title}</Button>
        </div>
      </Screen>
    );

  return (
    <Screen title={S.inbox.newTitle} back={scoped('/inbox', loc.scope)} lead={S.inbox.newLead}>
      <form class="stack form-narrow" data-gap="l" onSubmit={submit} noValidate>
        <div class="chips-choice" role="group" aria-label={S.inbox.newTitle}>
          {options.map((qk) => (
            <button
              key={qk}
              type="button"
              class="filter"
              aria-pressed={quick === qk}
              onClick={() => {
                setQuick(qk);
                if (!body.trim() || Object.values(S.inbox.quickText).includes(body)) setBody(S.inbox.quickText[qk]!);
                setTimeout(() => {
                  const el = area.current;
                  if (el) {
                    el.focus();
                    el.setSelectionRange(el.value.length, el.value.length);
                  }
                });
              }}
            >
              {S.inbox.quick[qk]}
            </button>
          ))}
        </div>
        <TextArea label={S.inbox.bodyLabel} value={body} onInput={setBody} maxLength={5000} rows={6} error={error} inputRef={area} />
        <div class="stack" data-gap="s">
          <input
            ref={input}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={async (e) => {
              const files = Array.from((e.currentTarget as HTMLInputElement).files ?? []).slice(0, 10 - photos.length);
              (e.currentTarget as HTMLInputElement).value = '';
              for (const f of files) {
                const idx = photos.length;
                setPhotos((ps) => [...ps, { name: f.name, uploadId: null }]);
                try {
                  const p = await processPhoto(f);
                  const id = await uploadBlob(p.blob, p.contentType, 'photo', 'request_attachment', () => undefined);
                  setPhotos((ps) => ps.map((x, i) => (i === idx ? { ...x, uploadId: id } : x)));
                } catch (err) {
                  toast(errorText(err, S.errors.offlineSend));
                  setPhotos((ps) => ps.filter((_, i) => i !== idx));
                }
              }
            }}
          />
          <Button icon="camera" onClick={() => input.current?.click()}>
            {S.inbox.attach}
          </Button>
          {photos.length > 0 && <p class="t-small t-muted" aria-live="polite">{S.inbox.attached(photos.filter((p) => p.uploadId).length)}</p>}
        </div>
        {act.error && <Banner tone="danger" role="alert" title={errorText(act.error, S.errors.offlineSend)} />}
        <div class="sticky-submit">
          <Button type="submit" variant="primary" size="lg" block busy={act.busy} icon="send">
            {S.inbox.send}
          </Button>
        </div>
      </form>
    </Screen>
  );
}

// ------------------------------------------------------------------------------------------ one request

function RequestView({ id }: { id: string }): JSX.Element {
  const s = useReady();
  const loc = useStore(location);
  const zone = useStore(tz);
  const q = useQuery('request', { params: { id } });
  const act = useSend();
  const [asking, setAsking] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const [reply, setReply] = useState('');
  const [confirmBack, setConfirmBack] = useState(false);
  if (q.error && !q.data) return <div class="screen"><LoadError error={q.error} onRetry={q.refetch} /></div>;

  const apply = (out: Detail | undefined, msg?: string) => {
    if (!out) return;
    haptic();
    setCached('request', { params: { id } }, () => out);
    invalidate('inbox', 'home');
    if (msg) toast(msg);
  };
  const r = q.data;
  const owner = s.membership.role === 'owner' ? null : S.errors.owner;

  return (
    <Screen title={r?.subject ?? S.inbox.title} back={scoped('/inbox', loc.scope)} query={q}>
      <Loaded q={q} skeleton="detail">
        {(r) => {
          const current = r.timeline.findLastIndex((t) => t.at !== null);
          return (
            <div class="stack" data-gap="l">
              <div class="cluster">
                <Chip tone={r.stage === 'ready_for_ok' ? 'new' : r.stage === 'live' ? 'ok' : 'neutral'}>{S.inbox.stage[r.stage]}</Chip>
                <span class="t-small t-muted">{S.inbox.number(r.number)}</span>
              </div>
              {r.promisedLabel && r.stage !== 'live' && r.stage !== 'closed' && <p>{S.inbox.promised(r.promisedLabel)}</p>}

              {r.proposal && r.proposal.needsYourOk && r.stage === 'ready_for_ok' && (
                <Card class="approve-card">
                  <h2 class="t-heading">{S.inbox.readyTitle}</h2>
                  <p>{S.inbox.readyLead}</p>
                  <p class="t-small t-muted">{S.inbox.readyWait}</p>
                  <h3 class="sub-h">{S.inbox.changes}</h3>
                  <ul role="list" class="changes">
                    {r.proposal.changes.map((c, i) => (
                      <li key={i}>
                        <strong>{c.label}:</strong> {c.after ?? S.inbox.none}
                        {c.before !== null && <span class="t-muted">, {S.inbox.was(c.before)}</span>}
                      </li>
                    ))}
                  </ul>
                  {r.proposal.googleChanges.length > 0 && (
                    <p class="t-small t-muted">
                      {S.inbox.google}: {r.proposal.googleChanges.join(', ')}
                    </p>
                  )}
                  {r.proposal.screenshots[0] && (
                    <BeforeAfter before={r.proposal.screenshots[0].before} after={r.proposal.screenshots[0].after} beforeLabel={S.inbox.before} afterLabel={S.inbox.after} toggleLabel={S.inbox.beforeAfter} alt={S.inbox.shotAlt} />
                  )}
                  {r.canApprove && can(s.membership, 'requests.approve') ? (
                    asking ? (
                      <div class="stack" data-gap="s">
                        <TextArea label={S.inbox.changeLabel} value={changeNote} onInput={setChangeNote} maxLength={2000} rows={3} />
                        <div class="btn-row" data-equal>
                          <Button variant="primary" busy={act.busy} disabled={changeNote.trim().length < 2} onClick={async () => apply(await act.run(() => send('requestChanges', { params: { id }, body: { proposalId: r.proposal!.id, note: changeNote.trim() } })), S.inbox.replySent)}>
                            {S.inbox.changeSend}
                          </Button>
                          <Button variant="quiet" onClick={() => setAsking(false)}>{S.app.cancel}</Button>
                        </div>
                      </div>
                    ) : (
                      <div class="btn-row" data-equal>
                        <Button variant="primary" size="lg" icon="check" busy={act.busy} onClick={async () => apply(await act.run(() => send('requestApprove', { params: { id }, body: { proposalId: r.proposal!.id } })), S.inbox.approved)}>
                          {S.inbox.approve}
                        </Button>
                        <Button size="lg" onClick={() => setAsking(true)}>{S.inbox.changeSomething}</Button>
                      </div>
                    )
                  ) : (
                    <Banner tone="info" icon="lock" title={S.inbox.notApprover(owner ?? S.errors.owner)} />
                  )}
                  {act.error && <p class="field-error" role="alert">{errorText(act.error, S.errors.offlineSend)}</p>}
                </Card>
              )}

              {r.stage === 'live' && (
                <Card class="live-card">
                  <h2 class="t-heading">{S.inbox.liveTitle}</h2>
                  <p>{S.inbox.liveLead}</p>
                  {r.proposal && r.proposal.changes.length > 0 && (
                    <ul role="list" class="changes">
                      {r.proposal.changes.map((c, i) => (
                        <li key={i}><strong>{c.label}:</strong> {c.after ?? S.inbox.none}</li>
                      ))}
                    </ul>
                  )}
                  <div class="btn-row">
                    {r.proposal?.previewUrl && <Button href={r.proposal.previewUrl} external iconEnd="external">{S.inbox.seeIt}</Button>}
                    {r.rollbackUntil && can(s.membership, 'requests.approve') && !confirmBack && (
                      <Button variant="secondary" icon="undo" onClick={() => setConfirmBack(true)}>{S.inbox.putBack}</Button>
                    )}
                  </div>
                  {r.rollbackUntil && <p class="t-small t-muted">{S.inbox.putBackUntil(shortDate(r.rollbackUntil, zone))}</p>}
                  {confirmBack && (
                    <div class="stack" data-gap="s">
                      <p>{S.inbox.putBackConfirm}</p>
                      <div class="btn-row" data-equal>
                        <Button variant="danger" busy={act.busy} onClick={async () => (apply(await act.run(() => send('requestRollback', { params: { id }, body: {} })), S.inbox.putBackDone), setConfirmBack(false))}>{S.inbox.putBack}</Button>
                        <Button variant="quiet" onClick={() => setConfirmBack(false)}>{S.app.cancel}</Button>
                      </div>
                    </div>
                  )}
                  {!r.rating && (
                    <div class="stack" data-gap="s">
                      <p><strong>{S.inbox.rateTitle}</strong></p>
                      <div class="btn-row" data-equal>
                        <Button onClick={async () => apply(await act.run(() => send('requestRating', { params: { id }, body: { rating: 'sorted' } })), S.inbox.rated)}>{S.inbox.sorted}</Button>
                        <Button onClick={async () => apply(await act.run(() => send('requestRating', { params: { id }, body: { rating: 'not_yet' } })), S.inbox.rated)}>{S.inbox.notYet}</Button>
                      </div>
                    </div>
                  )}
                </Card>
              )}

              <Section title={S.inbox.progress} id="req-progress">
                <Timeline
                  label={S.inbox.progress}
                  stateLabels={{ done: S.app.stateDone, current: S.app.stateCurrent, todo: S.app.stateTodo }}
                  steps={r.timeline.map((t, i) => ({
                    key: t.stage,
                    label: S.inbox.stage[t.stage],
                    time: t.at ? `${shortDate(t.at, zone)}, ${timeLabel(t.at, zone)}` : undefined,
                    state: i < current ? 'done' : i === current ? (t.stage === 'live' ? 'done' : 'current') : 'todo',
                  }))}
                />
              </Section>

              <Section title={S.inbox.thread} id="req-thread">
                <ol role="list" class="thread">
                  {r.messages.map((m) => (
                    <li key={m.id} data-author={m.author.kind}>
                      <p class="thread-who">
                        <strong>{m.author.kind === 'you' ? S.inbox.you : m.author.name}</strong> <span class="t-muted">· {rowTime(m.createdAt, zone)}</span>
                      </p>
                      <p class="thread-body">{m.body}</p>
                    </li>
                  ))}
                </ol>
                <form
                  class="stack reply-form"
                  data-gap="s"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!reply.trim()) return;
                    const m = await act.run(() => send('requestMessage', { params: { id }, body: { body: reply.trim() } }));
                    if (m) {
                      setCached('request', { params: { id } }, (prev) => (prev ? { ...prev, messages: [...prev.messages, m] } : prev));
                      setReply('');
                      toast(S.inbox.replySent);
                    }
                  }}
                >
                  <TextField label={S.inbox.replyLabel} value={reply} onInput={setReply} maxLength={5000} enterKeyHint="send" />
                  <Button type="submit" busy={act.busy} icon="send" disabled={!reply.trim()}>{S.inbox.reply}</Button>
                </form>
              </Section>
            </div>
          );
        }}
      </Loaded>
    </Screen>
  );
}

// ------------------------------------------------------------------------------------------ C-8 decision

function DecisionView({ id }: { id: string }): JSX.Element {
  const s = useReady();
  const loc = useStore(location);
  const q = useQuery('decision', { params: { id } });
  const act = useSend();
  const [name, setName] = useState('');
  const [nameErr, setNameErr] = useState<string | null>(null);
  if (q.error && !q.data) return <div class="screen"><LoadError error={q.error} onRetry={q.refetch} /></div>;
  const done = (out: z.infer<typeof Q.DecisionCard> | undefined, msg: string) => {
    if (!out) return;
    haptic();
    setCached('decision', { params: { id } }, () => out);
    invalidate('inbox', 'home');
    toast(msg);
  };
  return (
    <Screen title={q.data?.title ?? S.inbox.kind.decision!} back={scoped('/inbox', loc.scope)} query={q}>
      <Loaded q={q} skeleton="detail">
        {(d) => (
          <div class="stack" data-gap="l">
            <Card>
              <dl class="decision">
                <dt>{S.inbox.decisionWhat}</dt>
                <dd>
                  "{d.change.from ?? S.inbox.none}" <Icon name="arrow-right" size={18} /> "{d.change.to}"
                </dd>
                <dt>{S.inbox.decisionWhy}</dt>
                <dd>{d.why}</dd>
                <dt>{S.inbox.decisionRisks}</dt>
                <dd>
                  <ul class="bullets">
                    {d.risks.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </dd>
                <dt>{S.inbox.decisionDo}</dt>
                <dd>{d.whatWeWillDo}</dd>
              </dl>
            </Card>
            {d.state === 'sent' || d.state === 'will_approved' ? (
              d.canApprove && can(s.membership, 'decisions.approve') ? (
                <form
                  class="stack"
                  noValidate
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (name.trim().length < 2) {
                      setNameErr(S.inbox.typeNameError);
                      return;
                    }
                    setNameErr(null);
                    done(await act.run(() => send('decisionApprove', { params: { id }, body: { typedName: name.trim(), version: d.version } })), S.inbox.decisionApproved);
                  }}
                >
                  <TextField label={S.inbox.typeName} hint={S.inbox.typeNameHint} autoComplete="name" autoCapitalize="words" value={name} onInput={setName} error={nameErr} maxLength={80} />
                  <Button type="submit" variant="primary" size="lg" block busy={act.busy}>{S.inbox.approve}</Button>
                  <div class="btn-row" data-equal>
                    <Button onClick={async () => done(await act.run(() => send('decisionDecline', { params: { id }, body: { choice: 'not_now' } })), S.inbox.decisionDeclined)}>{S.inbox.notNow}</Button>
                    <Button onClick={async () => done(await act.run(() => send('decisionDecline', { params: { id }, body: { choice: 'talk_first' } })), S.inbox.decisionTalk)}>{S.inbox.talkFirst}</Button>
                  </div>
                  {act.error && <p class="field-error" role="alert">{errorText(act.error, S.errors.offlineSend)}</p>}
                </form>
              ) : (
                <Banner tone="info" icon="lock" title={S.inbox.notApprover(S.errors.owner)} />
              )
            ) : (
              <Banner tone={d.state === 'approved' || d.state === 'executed' ? 'ok' : 'info'} title={S.inbox.decisionClosed[d.state] ?? ''} />
            )}
          </div>
        )}
      </Loaded>
    </Screen>
  );
}
